export const AMAZON_REVIEWS_CAPABILITY = 'amazon.reviews.collect_by_url';
export const amazonReviewFilterKeys = ['published_after', 'published_before', 'min_rating', 'max_rating'] as const;
export type MarketAmazonReviewFilters = {
    published_after?: string; published_before?: string; min_rating?: number; max_rating?: number;
};
const object = (value: unknown): value is Record<string, unknown> =>
    value !== null && typeof value === 'object' && !Array.isArray(value);
export const validReviewDay = (value: unknown): value is string => {
    if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
    const parsed = new Date(value + 'T00:00:00.000Z');
    return Number.isFinite(parsed.getTime()) && parsed.toISOString().slice(0, 10) === value;
};
/** Review-level archive selection; never native search parameters or product aggregate ratings. */
export function normalizeAmazonReviewFilters(input: unknown): MarketAmazonReviewFilters {
    if (input == null) return {};
    if (!object(input) || Object.keys(input).some(key => !(amazonReviewFilterKeys as readonly string[]).includes(key)))
        throw new Error('Unsupported review filters');
    const out: MarketAmazonReviewFilters = {};
    for (const key of ['published_after', 'published_before'] as const) if (key in input) {
        if (!validReviewDay(input[key])) throw new Error('Invalid review calendar day');
        out[key] = input[key] as string;
    }
    if (out.published_after && out.published_before && out.published_after > out.published_before)
        throw new Error('Reversed review date bounds');
    for (const key of ['min_rating', 'max_rating'] as const) if (key in input) {
        if (!Number.isInteger(input[key]) || Number(input[key]) < 1 || Number(input[key]) > 5)
            throw new Error('Review rating requires integer stars from 1 to 5');
        out[key] = input[key] as number;
    }
    if (out.min_rating !== undefined && out.max_rating !== undefined && out.min_rating > out.max_rating)
        throw new Error('Reversed review star bounds');
    return out;
}
type Scope = { capability: string; source_system: string; scope: { kind: string; value?: string | null }; max_records_per_slice?: number; review_variant_scope?: "all" | "current" | null; result_filters?: unknown };
export function validAmazonReviewScopes(value: unknown): boolean {
    if (value == null) return true;
    if (!Array.isArray(value)) return false;
    try {
        for (const scope of value) if (object(scope) && scope.capability === AMAZON_REVIEWS_CAPABILITY) {
            if (scope.source_system !== 'amazon' || !object(scope.scope) || scope.scope.kind !== 'marketplace'
                || typeof scope.scope.value !== 'string' || !Number.isInteger(scope.max_records_per_slice)
                || Number(scope.max_records_per_slice) < 1 || Number(scope.max_records_per_slice) > 5000) return false;
            if (scope.review_variant_scope != null && !["all", "current"].includes(String(scope.review_variant_scope))) return false;
            normalizeAmazonReviewFilters(scope.result_filters);
        }
        return true;
    } catch { return false; }
}
export function amazonReviewScopesMatch(requested: Scope[], actual: unknown): boolean {
    const relevant = (scopes: Scope[]) => scopes.filter(scope => scope.capability === AMAZON_REVIEWS_CAPABILITY)
        .map(scope => ({ capability: scope.capability, source: scope.source_system, kind: scope.scope.kind, site: scope.scope.value,
            limit: scope.max_records_per_slice, variant: scope.review_variant_scope ?? "current", filters: normalizeAmazonReviewFilters(scope.result_filters) }))
        .sort((a, b) => String(a.site).localeCompare(String(b.site)));
    try { return Array.isArray(actual) && validAmazonReviewScopes(requested) && validAmazonReviewScopes(actual)
        && JSON.stringify(relevant(requested)) === JSON.stringify(relevant(actual)); }
    catch { return false; }
}
